import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { 
  useAddReviewMutation, 
  useUpdateReviewMutation 
} from '../../../redux/apis/endpoints/reviewApi';
import { useGetFulfillmentGroupDetailsQuery } from '../../../redux/apis/endpoints/orderApi';
import Button from '../../../components/ui/Button';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import OrderItemReviewSection from './OrderItemReviewSection';

const MultiItemReviewForm = ({ fulfillmentGroupId }) => {
  const history = useHistory();
  const user = useSelector(state => state.auth.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewForms, setReviewForms] = useState({});
  const [orderItemsList, setOrderItemsList] = useState([]);

  const [addReview] = useAddReviewMutation();
  const [updateReview] = useUpdateReviewMutation();
  
  // Fetch fulfillment group data
  const { data: fgData, isLoading: isLoadingFG } = useGetFulfillmentGroupDetailsQuery(fulfillmentGroupId, {
    skip: !fulfillmentGroupId
  });

  // Initialize order items and form state
  useEffect(() => {
    if (fgData && fgData.orderItems) {
      const items = fgData.orderItems.filter(it => it.status !== 'canceled');
      setOrderItemsList(items);

      // Initialize review form entries
      const formsInit = {};
      items.forEach(it => {
        formsInit[it.id] = {
          rating: 5,
          comment: '',
          pictures: [],
          initialized: false,
          existingReviewId: null
        };
      });
      setReviewForms(formsInit);
    }
  }, [fgData]);

  // Stable update function to prevent re-renders
  const updateForm = useCallback((orderItemId, changes) => {
    setReviewForms(prev => ({
      ...prev,
      [orderItemId]: {
        ...prev[orderItemId],
        ...changes
      }
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('You must be logged in to leave a review');
      return;
    }

    setIsSubmitting(true);
    
    try {
      let submittedCount = 0;
      let updatedCount = 0;
      
      for (const item of orderItemsList) {
        const form = reviewForms[item.id];
        if (!form || !form.comment || form.comment.trim().length < 10) {
          continue; // Skip incomplete reviews
        }

        const reviewPayload = {
          rating: form.rating,
          comment: form.comment.trim(),
          products: [item.product.id],
          customers: [parseInt(user.id, 10)],
          merchants: [item.product?.merchant?.id || item.merchant?.id].filter(Boolean),
          orders: [item.fulfillmentGroup?.order?.id].filter(Boolean),
          fulfillmentGroup: fulfillmentGroupId ? parseInt(fulfillmentGroupId, 10) : null,
          orderItem: item.id,
          pictures: form.pictures ? form.pictures.map(p => p.id) : [],
          isReviewCustomer: false,
          isReviewProduct: true
        };

        try {
          if (form.existingReviewId) {
            await updateReview({ id: form.existingReviewId, ...reviewPayload }).unwrap();
            updatedCount++;
          } else {
            await addReview(reviewPayload).unwrap();
            submittedCount++;
          }
        } catch (error) {
          console.error(`Error processing review for order item ${item.id}:`, error);
          toast.error(`Failed to process review for ${item.product?.name}: ${error.message}`);
        }
      }

      const totalProcessed = submittedCount + updatedCount;
      if (totalProcessed > 0) {
        let message = '';
        if (submittedCount > 0 && updatedCount > 0) {
          message = `${submittedCount} new review${submittedCount > 1 ? 's' : ''} submitted and ${updatedCount} review${updatedCount > 1 ? 's' : ''} updated successfully!`;
        } else if (submittedCount > 0) {
          message = `${submittedCount} review${submittedCount > 1 ? 's' : ''} submitted successfully!`;
        } else {
          message = `${updatedCount} review${updatedCount > 1 ? 's' : ''} updated successfully!`;
        }
        toast.success(message);
        history.goBack();
      } else {
        toast.error('No reviews were processed. Please complete at least one review with 10+ characters.');
      }
    } catch (err) {
      console.error('Failed to submit reviews:', err);
      toast.error('Failed to submit reviews');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingFG) {
    return <div className="max-w-4xl mx-auto p-4"><LoadingSpinner /></div>;
  }

  // Count complete and existing reviews
  const completeReviews = orderItemsList.filter(item => {
    const form = reviewForms[item.id];
    return form && form.comment && form.comment.trim().length >= 10;
  });

  const existingReviews = orderItemsList.filter(item => {
    const form = reviewForms[item.id];
    return form && form.existingReviewId;
  });

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Review Your Products</h1>
        <Button
          variant="outline"
          onClick={() => history.goBack()}
          disabled={isSubmitting}
          className="flex items-center gap-2"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back
        </Button>
      </div>

      <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>{completeReviews.length} of {orderItemsList.length}</strong> products have complete reviews. 
          Reviews need at least 10 characters to be submitted.
        </p>
        {existingReviews.length > 0 && (
          <p className="text-sm text-green-700 mt-1">
            <strong>{existingReviews.length}</strong> existing review{existingReviews.length > 1 ? 's' : ''} found and loaded for editing.
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        {orderItemsList.map(item => (
          <OrderItemReviewSection 
            key={item.id} 
            item={item} 
            data={reviewForms[item.id] || {rating:5,comment:'',pictures:[],initialized:false,existingReviewId:null}} 
            onChange={updateForm} 
          />
        ))}

        <div className="flex justify-between items-center pt-4 border-t">
          <Button
            variant="secondary"
            onClick={() => history.goBack()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            variant="primary" 
            isLoading={isSubmitting}
            disabled={completeReviews.length === 0}
          >
            {isSubmitting ? 'Processing...' : `${existingReviews.length > 0 ? 'Update & Submit' : 'Submit'} ${completeReviews.length} Review${completeReviews.length !== 1 ? 's' : ''}`}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MultiItemReviewForm; 